import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalMain,
  Pill,
  Select,
  Stack,
  Textarea,
} from '@sixfold/common-ui';
import { isNil, notNil } from '@sixfold/typed-primitives';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';

import { sortToGraphql } from './integrations_container';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { InfiniteScroll } from '../../components/infinitescroll';
import {
  getNodes,
  loadMoreFromConnection,
  TelematicsIntegrationDiagnosticStatus,
  TelematicsProviderContactInfo,
  TelematicsProviderQuery,
  TelematicsProviderQueryVariables,
  UpdateTelematicsIntegrationMutation,
  UpdateTelematicsIntegrationMutationVariables,
  UpdateTelematicsProviderMutation,
  UpdateTelematicsProviderMutationVariables,
} from '../../lib/graphql';
import { Loader, NetworkStatus } from '../../lib/loader';
import { embedURLsInText } from '../../lib/util/text';
import { IntegrationList, IntegrationListProps, useIntegrationListSort } from '../components/integration_list';
import { formatIntegrationType } from '../entities';
import {
  telematicsProviderQuery,
  updateTelematicsIntegrationMutation,
  updateTelematicsProviderMutation,
} from '../graphql';

import styles from './styles.module.css';

export interface ProviderIntegrationsProps {
  data: {
    name: string;
    integrationInstructionsURL: string | null;
    integrationType: string | null;
    isTourBasedProvider: boolean | null;
    isAddingIntegrationDisabled: boolean | null;
    isInternalUseOnly: boolean;
    isVehicleManagementEnabled: boolean | null;
    isExternalConfigurationNeeded: boolean | null;
    isGlobalAllocationForbidden: boolean | null;
    isDeprecated: boolean;
    contact_info: TelematicsProviderContactInfo | null;
    integrations: IntegrationListProps['integrations'];
    numIntegrations: number;
    numActiveIntegrations: number;
    numTotalTrucks: number;
    numActiveTrucks: number;
    recentAllocationsCount: number;
  } | null;
}

class LoadingContainer extends Loader<
  TelematicsProviderQuery,
  ProviderIntegrationsProps,
  TelematicsProviderQueryVariables
> {}

interface SetProviderInvalidProps {
  setPreRefreshProviderError: (isProviderInvalid: boolean) => void;
  onClick: () => Promise<void>;
}

interface ProviderContactInformationProps {
  contactInfo: TelematicsProviderContactInfo | null;
  providerId: string;
}

export const SetProviderInvalidButton: React.FC<SetProviderInvalidProps> = ({
  setPreRefreshProviderError,
  onClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onCancel = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onConfirm = useCallback(async () => {
    await onClick();
    setIsOpen(false);
    setPreRefreshProviderError(true);
  }, [onClick, setPreRefreshProviderError]);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return (
    <div>
      <Modal
        size="large"
        trigger={
          <Button onClick={() => setIsOpen(true)} kind="danger" style={{ float: 'right' }}>
            Set provider invalid
          </Button>
        }
        open={isOpen}
        onToggle={handleToggle}>
        <ModalHeader title="Are you sure?" />
        <ModalMain>
          <p>
            Please make sure you only proceed if you fully understand what you are doing, as reversal may be
            challenging.
          </p>
        </ModalMain>
        <ModalFooter>
          <Stack justifyContent="end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button iconEnd="small-check" onClick={onConfirm} kind="primary">
              Confirm
            </Button>
          </Stack>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const ProviderContactInformationButton: React.FC<ProviderContactInformationProps> = ({
  contactInfo,
  providerId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [email, setEmail] = useState(contactInfo?.email_address);
  const [comment, setComment] = useState(contactInfo?.comment);
  const [updatedAt, setUpdatedAt] = useState(contactInfo?.updated_at);

  const [updateTelematicsProvider] = useMutation<
    UpdateTelematicsProviderMutation,
    UpdateTelematicsProviderMutationVariables
  >(updateTelematicsProviderMutation, {
    onCompleted: (data) => {
      setEmail(data.updateTelematicsProvider?.contact_info?.email_address);
      setComment(data.updateTelematicsProvider?.contact_info?.comment);
      setUpdatedAt(data.updateTelematicsProvider?.contact_info?.updated_at);
    },
  });

  const onSave = useCallback(async () => {
    await updateTelematicsProvider({
      variables: {
        input: {
          telematics_provider_id: providerId,
          contact_info: {
            email_address: email ?? undefined,
            comment: comment ?? undefined,
          },
        },
      },
    });
    setIsEditing(false);
    setIsOpen(true);
  }, [email, comment, providerId, updateTelematicsProvider]);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsEditing(false);
    }
  }, [isOpen]);

  return (
    <div>
      <Modal
        size="xlarge"
        trigger={
          <Button
            size="small"
            hideLabel
            iconStart="small-email"
            onClick={() => setIsOpen(true)}
            style={{ marginLeft: 'auto', backgroundColor: 'var(--color-neutral-bg-accent-subtle-active)' }}>
            Email
          </Button>
        }
        onToggle={handleToggle}
        open={isOpen}>
        <ModalHeader title="Contact Information" />
        <ModalMain>
          <Stack direction="row" gap="medium">
            <Box style={{ flex: 1 }}>
              {isEditing ? (
                <Input
                  label="Email address"
                  name="email"
                  value={email ?? undefined}
                  onChange={(e) => setEmail(e.value)}
                  placeholder="Email address"
                />
              ) : (
                <div>
                  <p style={{ fontWeight: 'bold', color: 'black' }}>Email address</p>
                  <p>{notNil(email) && email !== '' ? email : 'No email address available'}</p>
                </div>
              )}
            </Box>
            <Box style={{ flex: 1 }}>
              {isEditing ? (
                <Textarea
                  label="Comment"
                  name="comment"
                  value={comment !== null ? comment : undefined}
                  onChange={(e) => setComment(e.value)}
                  placeholder="Comment"
                  rows={20}
                />
              ) : (
                <div>
                  <p style={{ fontWeight: 'bold', color: 'black' }}>Comment</p>

                  {notNil(comment) && comment !== '' ? (
                    comment?.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {embedURLsInText(line)}
                        <br />
                      </React.Fragment>
                    ))
                  ) : (
                    <p>No comments</p>
                  )}
                </div>
              )}
              {notNil(updatedAt) ? (
                <div style={{ textAlign: 'right', marginTop: '50px' }}>
                  <p>Updated at: {<FormattedDate date={updatedAt} />}</p>
                </div>
              ) : (
                ''
              )}
            </Box>
          </Stack>
        </ModalMain>
        <ModalFooter>
          <Stack justifyContent="end">
            {isEditing ? (
              <Stack direction="row">
                <Button color="grey" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button kind="primary" onClick={() => onSave()}>
                  Save
                </Button>
              </Stack>
            ) : (
              <Button iconStart="small-pencil" kind="primary" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </Stack>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const TelematicsProviderContainer = () => {
  const { provider_id } = useParams() as { provider_id: string };
  const { sort } = useIntegrationListSort();
  const [preRefreshProviderError, setPreRefreshProviderError] = useState(false);
  const [enabledFilter, setEnabledFilter] = useState<string | undefined>(undefined);
  const [diagnosticStatusFilter, setDiagnosticStatusFilter] = useState<string | undefined>(undefined);

  const result = useQuery<TelematicsProviderQuery, TelematicsProviderQueryVariables>(telematicsProviderQuery, {
    variables: {
      telematics_provider_id: provider_id,
      first: 2000,
      sort: sortToGraphql(sort),
    },
  });

  const [updateTelematicsIntegration] = useMutation<
    UpdateTelematicsIntegrationMutation,
    UpdateTelematicsIntegrationMutationVariables
  >(updateTelematicsIntegrationMutation);

  return (
    <LoadingContainer
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result={result as any}
      options={{
        showLoadingPlaceholder: ({ networkStatus }) => networkStatus === NetworkStatus.loading,
      }}
      mapData={({ data: { telematicsProvider } }) => ({
        data: notNil(telematicsProvider)
          ? {
              name: telematicsProvider.telematicsProviderName,
              integrationInstructionsURL: telematicsProvider.integrationInstructionsURL,
              integrationType: telematicsProvider.integrationType,
              isTourBasedProvider: telematicsProvider.isTourBasedProvider,
              isAddingIntegrationDisabled: telematicsProvider.isAddingIntegrationDisabled,
              isInternalUseOnly: telematicsProvider.isInternalUseOnly,
              isExternalConfigurationNeeded: telematicsProvider.isExternalConfigurationNeeded,
              isVehicleManagementEnabled: telematicsProvider.isVehicleManagementEnabled,
              isGlobalAllocationForbidden: telematicsProvider.isGlobalAllocationForbidden,
              isDeprecated: telematicsProvider.isDeprecated ?? false,
              contact_info: telematicsProvider.contact_info,
              integrations: getNodes(telematicsProvider.telematicsIntegrations.integrations),
              numIntegrations: telematicsProvider.telematicsIntegrations.count,
              numActiveIntegrations: telematicsProvider.enabledIntegrations.count,
              numTotalTrucks: telematicsProvider.totalTrucksCount,
              numActiveTrucks: telematicsProvider.activeTrucksCount,
              recentAllocationsCount: telematicsProvider.recentAllocationsCount,
            }
          : null,
      })}>
      {({ data }) => {
        const enabledCount = data?.integrations?.filter((i) => i.enabled).length ?? 0;
        const disabledCount = data?.integrations?.filter((i) => !i.enabled).length ?? 0;
        const enabledFailingCount =
          data?.integrations?.filter((i) => i.enabled && i.integrationSyncHistory?.at(-1)?.errorDescription != null)
            .length ?? 0;
        const anyDiagnosticCount = data?.integrations?.filter((i) => i.diagnosticStatus !== null).length ?? 0;
        const intermittentCount =
          data?.integrations?.filter((i) => i.diagnosticStatus === TelematicsIntegrationDiagnosticStatus.INTERMITTENT)
            .length ?? 0;
        const invalidCredentialsCount =
          data?.integrations?.filter(
            (i) => i.diagnosticStatus === TelematicsIntegrationDiagnosticStatus.INVALID_CREDENTIALS,
          ).length ?? 0;
        const investigatingCount =
          data?.integrations?.filter((i) => i.diagnosticStatus === TelematicsIntegrationDiagnosticStatus.INVESTIGATING)
            .length ?? 0;
        const providerErrorCount =
          data?.integrations?.filter((i) => i.diagnosticStatus === TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR)
            .length ?? 0;
        const noneCount = data?.integrations?.filter((i) => i.diagnosticStatus === null).length ?? 0;

        const enabledOptions = [
          { label: `Enabled (${enabledCount})`, value: 'true', disabled: enabledCount === 0 },
          {
            label: `Enabled - failing (${enabledFailingCount})`,
            value: 'enabled_failing',
            disabled: enabledFailingCount === 0,
          },
          { label: `Disabled (${disabledCount})`, value: 'false', disabled: disabledCount === 0 },
        ];
        const diagnosticOptions = [
          { label: `Any (${anyDiagnosticCount})`, value: 'ANY', disabled: anyDiagnosticCount === 0 },
          {
            label: `Intermittent (${intermittentCount})`,
            value: TelematicsIntegrationDiagnosticStatus.INTERMITTENT,
            disabled: intermittentCount === 0,
          },
          {
            label: `Invalid credentials (${invalidCredentialsCount})`,
            value: TelematicsIntegrationDiagnosticStatus.INVALID_CREDENTIALS,
            disabled: invalidCredentialsCount === 0,
          },
          {
            label: `Investigating (${investigatingCount})`,
            value: TelematicsIntegrationDiagnosticStatus.INVESTIGATING,
            disabled: investigatingCount === 0,
          },
          {
            label: `Provider error (${providerErrorCount})`,
            value: TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR,
            disabled: providerErrorCount === 0,
          },
          { label: `None (${noneCount})`, value: 'NULL', disabled: noneCount === 0 },
        ];

        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
            {data !== null && (
              <React.Fragment>
                <Stack direction="row" gap="medium">
                  <Box style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <h1 style={{ margin: 0, marginRight: '10px' }}>{data.name}</h1>
                      <ProviderContactInformationButton contactInfo={data.contact_info} providerId={provider_id} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      {data.isAddingIntegrationDisabled && <Pill kind="info">Adding integration disabled</Pill>}
                      {data.isInternalUseOnly && <Pill kind="warning">Internal use only</Pill>}
                      {data.isVehicleManagementEnabled && <Pill kind="info">Vehicle management enabled</Pill>}
                      {data.isExternalConfigurationNeeded && <Pill kind="info">External configuration needed</Pill>}
                      {data.isGlobalAllocationForbidden && <Pill kind="warning">Global allocation forbidden</Pill>}
                      {data.integrationType && (
                        <Pill kind="success">{formatIntegrationType(data.integrationType)}</Pill>
                      )}
                      {data.isTourBasedProvider && <Pill kind="info">Tour based provider</Pill>}
                      {data.isDeprecated && <Pill kind="danger">Deprecated</Pill>}
                    </div>
                    {data.integrationInstructionsURL !== null ? (
                      <div style={{ marginTop: 10 }}>
                        <a href={data.integrationInstructionsURL}>{data.integrationInstructionsURL}</a>
                      </div>
                    ) : (
                      '-'
                    )}
                  </Box>
                  <Box style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                    <div className={styles.statsContainer}>
                      <div className={styles.statsWrapper}>
                        <div className={styles.statItem}>
                          <div className={styles.statLabel}>Integrations</div>
                          <div>{data.numIntegrations}</div>
                        </div>
                        <div className={styles.statItem}>
                          <div className={styles.statLabel}>Active Integrations</div>
                          <div>{data.numActiveIntegrations}</div>
                        </div>
                        <div className={styles.statItem}>
                          <div className={styles.statLabel}>Total trucks</div>
                          <div>{data.numTotalTrucks}</div>
                        </div>
                        <div className={styles.statItem}>
                          <div className={styles.statLabel}>Active trucks</div>
                          <div>{data.numActiveTrucks}</div>
                        </div>
                        <div className={styles.statItem}>
                          <div className={styles.statLabel}>Allocations</div>
                          <div>{data.recentAllocationsCount}</div>
                        </div>
                      </div>
                      <SetProviderInvalidButton
                        setPreRefreshProviderError={setPreRefreshProviderError}
                        onClick={async () => {
                          if (notNil(data.integrations)) {
                            for (const integration of data.integrations) {
                              if (isNil(integration.diagnosticStatus) && integration.enabled) {
                                await updateTelematicsIntegration({
                                  variables: {
                                    input: {
                                      telematics_integration_id: integration.telematics_integration_id,
                                      company_integration_id: integration.company_integration_id,
                                      enabled: integration.enabled,
                                      diagnosticStatus: TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR,
                                    },
                                  },
                                });
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" className={styles.filtersContainer}>
                  <h2 className={styles.filtersHeading}>Integrations</h2>
                  <Select
                    label="Status"
                    labelPosition="hidden"
                    placeholder="Status"
                    isClearable
                    options={enabledOptions}
                    value={enabledOptions.find((o) => o.value === enabledFilter) ?? null}
                    onChange={(option) => {
                      setEnabledFilter(option?.value);
                    }}
                    className={styles.filterDropdown}
                  />
                  <Select
                    label="Diagnostic status"
                    labelPosition="hidden"
                    placeholder="Diagnostic status"
                    isClearable
                    options={diagnosticOptions}
                    value={diagnosticOptions.find((o) => o.value === diagnosticStatusFilter) ?? null}
                    onChange={(option) => {
                      setDiagnosticStatusFilter(option?.value);
                    }}
                    className={styles.diagnosticFilterDropdown}
                  />
                </Stack>
                <InfiniteScroll
                  className="integrations-list"
                  triggerOnWindow={false}
                  loadMoreEntries={() =>
                    loadMoreFromConnection(telematicsProviderQuery, result, [
                      'telematicsProvider',
                      'telematicsIntegrations',
                      'integrations',
                    ])
                  }>
                  <IntegrationList
                    integrations={
                      data.integrations?.filter((integration) => {
                        if (enabledFilter !== undefined) {
                          if (enabledFilter === 'true' && !integration.enabled) return false;
                          if (enabledFilter === 'false' && integration.enabled) return false;
                          if (enabledFilter === 'enabled_failing') {
                            if (!integration.enabled) return false;
                            if (integration.integrationSyncHistory?.at(-1)?.errorDescription == null) return false;
                          }
                        }
                        if (diagnosticStatusFilter !== undefined) {
                          if (diagnosticStatusFilter === 'ANY') {
                            return integration.diagnosticStatus !== null;
                          }
                          if (diagnosticStatusFilter === 'NULL') {
                            return integration.diagnosticStatus === null;
                          }
                          return integration.diagnosticStatus === diagnosticStatusFilter;
                        }
                        return true;
                      }) ?? null
                    }
                    preRefreshProviderError={preRefreshProviderError}
                  />
                </InfiniteScroll>
              </React.Fragment>
            )}
          </div>
        );
      }}
    </LoadingContainer>
  );
};
